import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGlobe, FaGithub, FaShoppingCart, FaUtensils, FaCode } from 'react-icons/fa';

const ProjectsContainer = styled.div`
  padding: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

const SubTitle = styled(motion.p)`
  color: #666;
  letter-spacing: 2px;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const Title = styled(motion.h2)`
  font-size: 2rem;
  margin-bottom: 2rem;
  font-family: 'Playfair Display', serif;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProjectCard = styled(motion.div)`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    max-width: 400px;
    margin: 0 auto;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  }
`;

const ProjectImage = styled.div`
  height: 120px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;

  @media (max-width: 768px) {
    height: 100px;
    font-size: 1.5rem;
  }
`;

const ProjectContent = styled.div`
  padding: 0.8rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.3rem;
  font-family: 'Playfair Display', serif;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ProjectDescription = styled.p`
  color: #666;
  line-height: 1.3;
  margin-bottom: 0.8rem;
  font-size: 0.8rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const TechStack = styled.div`
  display: flex;
  gap: 0.3rem;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const TechTag = styled.span`
  background: #f0f0f0;
  padding: 0.15rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
  color: #555;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 0.8rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  text-decoration: none;
  color: #2196F3;
  font-weight: 500;
  font-size: 0.8rem;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    gap: 0.4rem;
  }

  &:hover {
    color: #1976D2;
  }
`;

const projects = [
  {
    title: "E-commerce Shopping Platform",
    description: "Full-featured e-commerce platform with product catalog, shopping cart, and secure checkout functionality.",
    color: "#E91E63",
    icon: <FaShoppingCart />,
    tech: ["HTML", "CSS", "JAVASCRIPT", "LocalStorage"],
    liveLink: "https://shlok2k.github.io/shopping/",
    githubLink: "https://github.com/Shlok2k/shopping"
  },
  {
    title: "Recipe Hub",
    description: "Interactive recipe application with search functionality, filtering options, and step-by-step cooking instructions.",
    color: "#FF9800",
    icon: <FaUtensils />,
    tech: ["React", "API Integration", "CSS Grid"],
    liveLink: "https://resplendent-frangipane-b5840f.netlify.app/",
    githubLink: "https://github.com/Shlok2k/Recipe_finder/blob/main/README.md"
  },
  {
    title: "Library Management System",
    description: "Comprehensive task management system with real-time updates, drag-and-drop functionality, and team collaboration features.",
    color: "#4CAF50",
    icon: <FaCode />,
    tech: ["React", "Redux", "Firebase", "Material-UI"],
    liveLink: "https://github.com/Shlok2k/LibraryManagement-main/blob/main/LibraryManagement-main/README.md",
    githubLink: "https://github.com/Shlok2k/LibraryManagement-main"
  },
  {
    title: "Weather SkyAlert",
    description: "Real-time weather application with location-based forecasts, interactive maps, and detailed weather analytics.",
    color: "#9C27B0",
    icon: <FaCode />,
    tech: ["React", "OpenWeather API", "Chart.js"],
    liveLink: "https://683ea2ccdb92ae89c5d1eec5--willowy-tiramisu-a46171.netlify.app/",
    githubLink: "https://github.com/Shlok2k/Sky_alert/blob/main/README.md"
  },
  {
    title: "Interactive Chatbot",
    description: "A responsive chatbot interface with natural language processing capabilities, built using vanilla JavaScript. Features include message history, typing indicators, and custom responses.",
    color: "#00BCD4",
    icon: <FaCode />,
    tech: ["HTML5", "CSS3", "JavaScript", "LocalStorage"],
    liveLink: "https://shlok2k.github.io/Aichatbot/",
    githubLink: "https://github.com/Shlok2k/Aichatbot"
  },
  {
    title: "GenZ Career Aspirations Dashboard",
    description: "Interactive data visualization dashboard analyzing GenZ career preferences and trends. Features include dynamic charts, statistical analysis, and insights generation.",
    color: "#FF5722",
    icon: <FaCode />,
    tech: ["Python", "Pandas", "Plotly", "Dash", "Seaborn", "Matplotlib"],
    liveLink: "https://github.com/Shlok2k/Data-Analyst/blob/main/README.md",
    githubLink: "https://github.com/Shlok2k/Data-Analyst"
  }
];

const Projects = () => {
  return (
    <ProjectsContainer>
      <Content>
        <SubTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          MY WORK
        </SubTitle>
        <Title
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          RECENT PROJECTS
        </Title>

        <ProjectsGrid>
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProjectImage color={project.color}>
                {project.icon}
              </ProjectImage>
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <TechStack>
                  {project.tech.map((tech, i) => (
                    <TechTag key={i}>{tech}</TechTag>
                  ))}
                </TechStack>
                <ProjectLinks>
                  <ProjectLink href={project.liveLink} target="_blank">
                    <FaGlobe /> Live Demo
                  </ProjectLink>
                  <ProjectLink href={project.githubLink} target="_blank">
                    <FaGithub /> Source Code
                  </ProjectLink>
                </ProjectLinks>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Content>
    </ProjectsContainer>
  );
};

export default Projects; 